import { useState, useMemo } from "react";
import { functions, db } from "@/lib/firebase";
import { httpsCallable } from "firebase/functions";
import { doc, getDoc } from "firebase/firestore";
import { Checkout } from 'capacitor-razorpay';
import { useAuth } from "@/hooks/use-auth";
import { useData } from "@/contexts/DataContext";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import {
  ChevronLeft,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Loader2,
  ArrowRight,
  ShieldCheck,
  Zap
} from "lucide-react";

import { PRO_PLANS, ULTIMATE_PLAN } from "@/lib/constants";
import { Crown } from "lucide-react";

export default function SubscriptionPage() {
  const { user } = useAuth();
  const { businessProfile, plan, trialsUsed } = useData();
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  const [loadingPlan, setLoadingPlan] = useState<number | null>(null);
  const [selectedPlan, setSelectedPlan] = useState(PRO_PLANS[2]);
  const [expandedPlanDay, setExpandedPlanDay] = useState<number | null>(null);

  const hasCurrentPro = useMemo(() => {
    if (!plan?.expiresAt) return false;
    return new Date(plan.expiresAt).getTime() > new Date().getTime() && (plan.tier === "pro" || plan.tier === "ultimate");
  }, [plan]);

  const hasUsedTrial = trialsUsed?.any === true;
  const isEligibleForTrial = !hasCurrentPro && !hasUsedTrial;

  const handleUltimateAction = async () => {
    if (!user) {
      toast({ title: "Please login first", variant: "destructive" });
      return;
    }
    setLoadingPlan(365);
    try {
      // Trial path — call activateTrial directly, no payment
      if (isEligibleForTrial) {
        const activateTrial = httpsCallable(functions, "activateTrial");
        await activateTrial({ tier: "ultimate" });
        toast({ title: "30-Day Ultimate Trial Activated!", description: "Enjoy full Desktop access — ad-free everywhere." });
        setLocation("/");
        return;
      }

      // Paid path — create Razorpay order for one-time yearly
      const createOrder = httpsCallable(functions, "createRazorpayOrder");
      const resp = await createOrder({
        itemId: "ultimate_yearly",
        amount: ULTIMATE_PLAN.price,
      });
      const data = resp.data as { success: boolean; orderId: string };
      if (!data.success || !data.orderId) throw new Error("Could not initialize Ultimate payment");

      const rzpKey = "rzp_live_StU8i2gpLCYQOq";
      const options: any = {
        key: rzpKey,
        order_id: data.orderId,
        name: "ASL DIGITAL SOLUTIONS",
        description: "Ultimate Yearly Plan",
        currency: "INR",
        amount: (ULTIMATE_PLAN.price * 100).toString(),
        theme: { color: "#8B1A1A" },
        notes: { itemId: "ultimate_yearly", userId: user.uid },
      };
      await Checkout.open(options);
      setLocation("/payment-status");
    } catch (err: any) {
      console.error("Ultimate purchase error:", err);
      if (err.code !== 0 && err.message !== "Payment cancelled by user") {
        toast({ title: "Payment Error", description: err.message || "Try again.", variant: "destructive" });
      }
    } finally {
      setLoadingPlan(null);
    }
  };

  const handleSubscribe = async () => {
    if (!user) {
      toast({ title: "Please login first", variant: "destructive" });
      return;
    }

    setLoadingPlan(selectedPlan.days);

    try {
      const userDocRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userDocRef);

      const baselineExpiry = userSnap.data()?.settings?.plan?.expiresAt || "";
      localStorage.setItem("dh_baseline_expiry", baselineExpiry);

      const createSubscription = httpsCallable(functions, 'createRazorpaySubscription');

      const result = await createSubscription({
        days: selectedPlan.days,
        targetTier: "pro",
      });

      const data = result.data as { success: boolean; subscriptionId: string };

      if (!data.success || !data.subscriptionId) throw new Error("Could not initialize payment");

      // 🛡️ HARDCODED LIVE KEY
      const rzpKey = "rzp_live_StU8i2gpLCYQOq";

      const options: any = {
        key: rzpKey,
        subscription_id: data.subscriptionId,
        name: "ASL DIGITAL SOLUTIONS",
        description: `${selectedPlan.days} Days Premium Access`,
        currency: "INR",
        amount: (selectedPlan.price * 100).toString(),
        theme: { color: "#8B1A1A" }
      };

      const prefill: any = {};
      const contactNum = businessProfile?.businessPhone?.replace(/\D/g, '').slice(-10) || user.phoneNumber?.replace(/\D/g, '').slice(-10);
      if (contactNum && contactNum.length >= 10) prefill.contact = contactNum;

      const emailStr = businessProfile?.businessEmail || user.email;
      if (emailStr && emailStr.trim() !== "") prefill.email = emailStr.trim();

      const nameStr = businessProfile?.ownerName || businessProfile?.businessName || user.displayName;
      if (nameStr && nameStr.trim() !== "") prefill.name = nameStr.trim();

      if (Object.keys(prefill).length > 0) {
        options.prefill = prefill;
      }

      await Checkout.open(options);
      setLocation("/payment-status");

    } catch (error: any) {
      console.error("Native Payment Error:", error);
      if (error.code !== 0 && error.message !== "Payment cancelled by user") {
        toast({ title: "Payment Error", description: error.message || "The process was interrupted.", variant: "destructive" });
      }
    } finally {
      setLoadingPlan(null);
    }
  };

  // Active Ultimate users have already maxed out — block the upgrade UI and
  // direct them to support for any plan changes.
  const isActiveUltimate =
    plan?.tier === "ultimate" && !!plan?.expiresAt && new Date(plan.expiresAt) > new Date();
  if (isActiveUltimate) {
    return (
      <div className="min-h-screen bg-[#FFFBF2] dark:bg-[#1A1614] flex flex-col"
        style={{
          paddingTop: "calc(env(safe-area-inset-top) + 1.25rem)",
          paddingBottom: "calc(env(safe-area-inset-bottom) + 1.5rem)",
        }}
      >
        <div className="px-4">
          <button
            onClick={() => setLocation("/settings")}
            className="rounded-full p-2 border border-slate-200 dark:border-[#454545] bg-white dark:bg-[#1F1B19]/60"
          >
            <ChevronLeft className="h-4 w-4 text-[#0F172A] dark:text-[#EEEAE3]" />
          </button>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center max-w-md mx-auto">
          <div className="rounded-3xl p-5 mb-5 border border-[#D4AF37]/30 bg-[#D4AF37]/15">
            <Crown className="h-12 w-12 text-[#D4AF37]" strokeWidth={1.5} />
          </div>
          <h1 className="text-3xl font-black uppercase tracking-tighter leading-[0.95] text-[#0F172A] dark:text-[#EEEAE3] mb-3">
            HIGHEST<br /><span className="text-[#8B1A1A]">PLAN ACTIVE</span>
          </h1>
          <p className="text-sm text-slate-500 dark:text-[#8A8A92] leading-relaxed mb-6">
            You're on the Ultimate plan — the top tier. Upgrades not available. To downgrade or change billing, contact support.
          </p>
          <button
            onClick={() => setLocation("/support")}
            className="w-full rounded-2xl bg-[#8B1A1A] hover:bg-[#631212] py-4 px-6 text-sm font-black uppercase tracking-wider text-white active:scale-[0.99] transition-transform"
          >
            Contact Support
          </button>
          <button
            onClick={() => setLocation("/billing")}
            className="mt-3 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-[#8A8A92]"
          >
            View Billing
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#121212] pb-28 font-sans">
      <header className="px-4 py-4 flex items-center justify-between bg-white dark:bg-slate-900 sticky top-0 z-30 shadow-sm">
        <div className="flex items-center">
          <button onClick={() => setLocation("/settings")} className="w-8 h-8 flex items-center justify-center active:scale-95 transition-transform">
            <ChevronLeft className="w-7 h-7 text-slate-900 dark:text-white" />
          </button>
          <h1 className="ml-2 text-lg font-bold text-slate-900 dark:text-white tracking-tight">DailyHisaab Premium</h1>
        </div>
        <button onClick={() => setLocation("/support")} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-700 text-sm font-semibold text-slate-700 dark:text-slate-300 active:bg-slate-100 transition-colors">
          <HelpCircle className="w-4 h-4" /> Help
        </button>
      </header>

      <div className="bg-gradient-to-br from-[#FFFDF6] to-[#FFF5E6] dark:from-slate-800 dark:to-slate-900 p-6 pt-8 pb-10 relative overflow-hidden border-b border-amber-100 dark:border-slate-800">
        <h2 className="text-3xl font-black text-slate-900 dark:text-white leading-[1.1] tracking-tight relative z-10 w-3/4">
          {isEligibleForTrial
            ? "Go Premium completely free for 7 days!"
            : "Save up to 50% — remove all ads when you subscribe!"}
        </h2>
        <div className="absolute -right-6 -bottom-6 w-32 h-32 bg-[#8B1A1A] rounded-[2rem] rotate-12 flex items-center justify-center shadow-xl opacity-90">
          <ShieldCheck className="w-16 h-16 text-white -rotate-12" />
        </div>
      </div>

      <div className="px-4 py-6">
        <h3 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-4 ml-1">
          Ultimate — Mobile + Desktop
        </h3>

        <div
          className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#8B1A1A] via-[#631212] to-[#4A0E0E] shadow-xl mb-6"
        >
          <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-white/10 blur-3xl pointer-events-none" />
          <div className="p-5 relative">
            <div className="flex items-center gap-2 mb-2">
              <Crown className="w-5 h-5 text-amber-300" />
              <h3 className="text-lg font-bold text-white tracking-wide">{ULTIMATE_PLAN.label}</h3>
            </div>
            <div className="flex items-baseline gap-2 mb-3">
              <span className="text-3xl font-black text-white">₹{ULTIMATE_PLAN.price}</span>
              <span className="text-base font-bold text-white/60 line-through">₹{ULTIMATE_PLAN.originalPrice}</span>
              <span className="text-[10px] font-black text-amber-300 uppercase tracking-widest">/year · one-time</span>
            </div>
            <div className="space-y-1.5 mb-4">
              {ULTIMATE_PLAN.features.map((f, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-amber-300 mt-0.5">✓</span>
                  <span className="text-xs text-white/90">{f}</span>
                </div>
              ))}
            </div>
            <button
              onClick={handleUltimateAction}
              disabled={loadingPlan !== null}
              className="w-full bg-amber-400 hover:bg-amber-500 text-black font-black text-sm h-12 rounded-xl shadow-sm flex items-center justify-center gap-2 active:scale-95 transition-all disabled:opacity-50"
            >
              {loadingPlan === 365 ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isEligibleForTrial ? (
                <>Activate 30-Day Free Trial <ArrowRight className="w-4 h-4" /></>
              ) : (
                <>Subscribe — ₹{ULTIMATE_PLAN.price}/year <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
          </div>
        </div>

        <h3 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-4 ml-1">
          Premium — Mobile Only
        </h3>

        <div className="space-y-4">
          {PRO_PLANS.map((plan) => {
            const isSelected = selectedPlan.days === plan.days;
            const isExpanded = expandedPlanDay === plan.days;

            return (
              <div
                key={plan.days}
                onClick={() => setSelectedPlan(plan)}
                className={`relative rounded-2xl overflow-hidden transition-all cursor-pointer ${isSelected
                  ? "ring-2 ring-amber-400 ring-offset-2 ring-offset-slate-50 dark:ring-offset-[#121212]"
                  : "border border-slate-200 dark:border-slate-800"
                  } bg-[#1E293B] shadow-lg`}
              >
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-bold text-white tracking-wide">{plan.label}</h3>
                    <Zap className="w-4 h-4 text-amber-400 fill-amber-400" />
                  </div>

                  <div className="flex items-baseline gap-2 mb-5">
                    <span className="text-2xl font-black text-white">₹{plan.price}</span>
                    <span className="text-base font-bold text-slate-400 line-through">₹{plan.originalPrice}</span>
                  </div>

                  <div className="grid grid-cols-3 gap-2 border-t border-slate-700/50 pt-4">
                    <div className="text-center flex flex-col items-center">
                      <span className="text-[11px] text-slate-400 mb-1">Discount</span>
                      <span className="text-sm font-bold text-white">{plan.discount}</span>
                    </div>
                    <div className="text-center flex flex-col items-center border-l border-slate-700/50">
                      <span className="text-[11px] text-slate-400 mb-1">Validity</span>
                      <span className="text-sm font-bold text-white uppercase">{plan.days} Days</span>
                    </div>
                    <div className="text-center flex flex-col items-center border-l border-slate-700/50">
                      <span className="text-[11px] text-slate-400 mb-1">Total Bills</span>
                      <span className="text-[11px] font-bold text-white uppercase mt-0.5 tracking-wider">Unlimited</span>
                    </div>
                  </div>
                </div>

                <div className="bg-[#0F172A]">
                  <div
                    className="flex justify-end px-5 py-3 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedPlanDay(isExpanded ? null : plan.days);
                    }}
                  >
                    <span className="text-xs font-semibold text-slate-300 flex items-center gap-1">
                      {isExpanded ? "Less Details" : "Terms and Conditions"}
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </span>
                  </div>

                  {isExpanded && (
                    <div className="px-6 pb-5 pt-1 space-y-2">
                      <p className="text-xs text-slate-400 mb-2">Features included:</p>
                      {plan.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <span className="text-slate-500 mt-0.5">•</span>
                          <span className="text-xs text-slate-300 leading-relaxed">{feature}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 p-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] flex justify-between items-center shadow-[0_-4px_15px_rgba(0,0,0,0.05)] z-50">
        <div className="flex flex-col px-2">
          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">{selectedPlan.label}</span>
          <span className="text-2xl font-black text-slate-900 dark:text-white leading-none mt-1">₹{selectedPlan.price}</span>
        </div>
        <button
          onClick={handleSubscribe}
          disabled={loadingPlan !== null}
          className="bg-amber-400 hover:bg-amber-500 text-black font-black text-base h-[52px] px-6 rounded-xl shadow-sm flex items-center justify-center gap-2 active:scale-95 transition-all min-w-[180px]"
        >
          {loadingPlan !== null ? (
            <Loader2 className="animate-spin w-5 h-5 text-black" />
          ) : (
            <>
              {isEligibleForTrial ? "Start 7-Day Free Trial" : "Subscribe"}
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}